import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { Users, UserPlus, CheckCircle, XCircle, Loader2, X } from 'lucide-react'
import { PageHero, Button, Input } from '@components/ui'
import { userService, CreateUserRequest } from '@services/userService'
import { useAuthStore } from '@store/useAuthStore'

export function WorkshopManagementPage() {
  const { user } = useAuthStore()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'active' | 'inactive'>('active')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newUser, setNewUser] = useState<CreateUserRequest>({
    name: '',
    email: '',
    password: '',
    active: true,
    functionId: 1, // Default: Rececionista
    workshopCompanyId: user?.workshopCompanyId,
    phone: '',
  })

  const workshopCompanyId = user?.workshopCompanyId

  // Buscar utilizadores ativos
  const { data: activeUsers = [], isLoading: isLoadingActive } = useQuery(
    ['users', 'active', workshopCompanyId],
    () => userService.getActiveByWorkshop(workshopCompanyId!),
    { enabled: !!workshopCompanyId && activeTab === 'active' }
  )

  // Buscar utilizadores inativos
  const { data: inactiveUsers = [], isLoading: isLoadingInactive } = useQuery(
    ['users', 'inactive', workshopCompanyId],
    () => userService.getInactiveByWorkshop(workshopCompanyId!),
    { enabled: !!workshopCompanyId && activeTab === 'inactive' }
  )

  // Mutação para criar utilizador
  const createUserMutation = useMutation(
    (data: CreateUserRequest) => userService.create(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users'])
        setShowCreateModal(false)
        setNewUser({
          name: '',
          email: '',
          password: '',
          active: true,
          functionId: 1,
          workshopCompanyId: user?.workshopCompanyId,
          phone: '',
        })
      },
    }
  )

  // Mutação para ativar/desativar utilizador
  const toggleUserMutation = useMutation(
    (userId: number) => userService.toggleStatus(userId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users'])
      },
      onError: (err: any) => {
        const msg = err?.response?.data?.error || 'Não foi possível alterar o status.'
        alert(msg)
      },
    }
  )

  const currentUsers = activeTab === 'active' ? activeUsers : inactiveUsers
  const isLoadingUsers = activeTab === 'active' ? isLoadingActive : isLoadingInactive

  const functionNames: Record<number, string> = {
    1: 'Rececionista',
    2: 'Mecanico',
    3: 'Administrador',
  }

  const handleCreateUser = () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      alert('Por favor, preencha todos os campos obrigatórios')
      return
    }
    if (!workshopCompanyId) {
      alert('Utilizador sem oficina associada. Não pode criar utilizadores.')
      return
    }
    createUserMutation.mutate({ ...newUser, workshopCompanyId })
  }

  const handleToggleUser = (userId: number) => {
    if (confirm('Tem certeza que deseja alterar o status deste utilizador?')) {
      toggleUserMutation.mutate(userId)
    }
  }

  return (
    <div className="space-y-8">
      <PageHero title="GESTÃO DA OFICINA" />

      {/* Gestão de Utilizadores */}
      <section className="rounded-[32px] border border-secondary-200 bg-white p-6 shadow-lg sm:p-8">
        <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="grid grid-cols-2 gap-2 md:flex md:items-center md:gap-4">
            <Button
              variant={activeTab === 'active' ? 'primary' : 'outline'}
              onClick={() => setActiveTab('active')}
              className="flex items-center justify-center gap-2 whitespace-nowrap"
            >
              <CheckCircle className="h-4 w-4" />
              Ativos ({activeUsers.length})
            </Button>
            <Button
              variant={activeTab === 'inactive' ? 'primary' : 'outline'}
              onClick={() => setActiveTab('inactive')}
              className="flex items-center justify-center gap-2 whitespace-nowrap"
            >
              <XCircle className="h-4 w-4" />
              Inativos ({inactiveUsers.length})
            </Button>
          </div>
          <Button
            variant="primary"
            onClick={() => setShowCreateModal(true)}
            className="flex w-full items-center justify-center gap-2 whitespace-nowrap md:w-auto"
          >
            <UserPlus className="h-4 w-4" />
            Criar Utilizador
          </Button>
        </div>

        {/* Lista de Utilizadores */}
        {isLoadingUsers ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-secondary-500" />
          </div>
        ) : currentUsers.length === 0 ? (
          <div className="py-12 text-center text-secondary-500">
            <Users className="mx-auto h-12 w-12 mb-4 text-secondary-400" />
            <p>Não há utilizadores {activeTab === 'active' ? 'ativos' : 'inativos'}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {currentUsers.map((user) => (
              <div
                key={user.id}
                className="flex flex-col gap-3 rounded-lg border border-secondary-200 bg-white p-4 transition-colors hover:bg-secondary-50 md:flex-row md:items-center md:justify-between"
              >
                <div className="flex min-w-0 items-center gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100">
                    <Users className="h-5 w-5 text-primary-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-secondary-900">{user.name}</p>
                    <p className="break-all text-sm text-secondary-500 md:break-normal">{user.email}</p>
                    <p className="text-xs text-secondary-400">
                      {functionNames[user.function || user.functionId || 1]}
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleToggleUser(user.id)}
                  disabled={toggleUserMutation.isLoading}
                  className="w-full whitespace-nowrap md:w-auto"
                >
                  {user.active ? 'Desativar' : 'Ativar'}
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Modal de Criação de Utilizador */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-2xl border border-secondary-200 bg-white p-6 shadow-xl">
            <div className="mb-6 flex items-center justify-between">
              <h3 className="text-xl font-bold text-secondary-900">Criar Novo Utilizador</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-secondary-400 hover:text-secondary-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-secondary-700">Nome</label>
                <Input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  placeholder="Nome completo"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-secondary-700">Email</label>
                <Input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="email@exemplo.com"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-secondary-700">Senha</label>
                <Input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="Senha"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-secondary-700">Telemóvel</label>
                <Input
                  type="tel"
                  value={newUser.phone ?? ''}
                  onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                  placeholder="912345678"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-secondary-700">
                  Função <span className="text-red-500">*</span>
                </label>
                <select
                  value={newUser.functionId}
                  onChange={(e) => setNewUser({ ...newUser, functionId: Number.parseInt(e.target.value, 10) })}
                  className="w-full rounded-lg border border-secondary-200 bg-white px-3 py-2.5 text-sm text-secondary-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100 transition-colors hover:border-secondary-300"
                >
                  <option value={1}>Rececionista</option>
                  <option value={2}>Mecânico</option>
                  <option value={3}>Administrador</option>
                </select>
              </div>

              <div className="flex items-center gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  variant="primary"
                  onClick={handleCreateUser}
                  isLoading={createUserMutation.isLoading}
                  className="flex-1"
                >
                  Criar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}







